import React, { useState, useRef, useCallback } from 'react';

import config from 'config';
import PropTypes from 'prop-types';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import styled, { css } from 'styled-components';

import ForgotPasswordForm from '~/login/components/ForgotPasswordForm';
import LoginSlidingPanels from '~/login/components/LoginSlidingPanels';
import WelcomeBackWrapper from '~/login/components/WelcomeBackWrapper';
import Link from '~/shared/components/Link';
import OverflowIndicator from '~/shared/components/OverflowIndicator';
import Text from '~/shared/components/Text';
import featureNames from '~/shared/constants/featureFlags';
import { getPromoPricingActiveAsset } from '~/shared/utils/getPromoPricingActiveAsset';
import { getPromoPricingUIAssetContainerName } from '~/shared/utils/promoPricingUI';
import scrollToRef from '~/shared/utils/scrollToRef';
import useHasFeature from '~/shared/utils/useHasFeature';
import { Router } from '~/techstyle-shared/next-routes';
import { useAsset } from '~/techstyle-shared/react-assets';
import {
  Button,
  desktop,
  mobile,
  SlidingPanels,
  useBreakpoint,
  ProductImage as BaseProductImage,
  useProductDetailContext,
} from '~/techstyle-shared/react-components';
import { FormattedMessage } from '~/techstyle-shared/react-intl';
import { useDomain } from '~/techstyle-shared/redux-core';

import useGuessLoggedOutCustomerGenderFromProduct from '../../utils/useGuessLoggedOutCustomerGenderFromProduct';
import { useProductDetailCTAContext } from '../ProductDetailCTAs';
import ProductOverlay from '../ProductOverlay';
import SpeedySignupForm from '../SpeedySignupForm';

const speedySignupLayoutEnhanced = config.get(
  'public.brand.speedySignupLayoutEnhanced'
);

const speedySignupLoginRedirect = config.get(
  'public.brand.speedySignupLoginRedirect'
);

const PanelStates = {
  SIGN_UP: 0,
  LOGIN: 1,
  FORGOT_PASSWORD: 2,
  EMAIL_SENT: 3,
};

const SlidingPanelsSection = styled.section`
  height: 100%;
  align-self: stretch;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  &:focus {
    outline: none;
  }
`;

const SlidingPanel = styled.div`
  --speedySignupPanelPadding: clamp(24px, 5vh, 52px);
  width: 100%;
  height: 100%;
  max-height: calc(100vh - 48px);
  ${mobile`
    padding: 0;
    min-height: 100%;
  `}
`;

const panelWithFooterStyles = css`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
`;

const SignUpFormSlidingPanel = styled(SlidingPanel)`
  ${panelWithFooterStyles}
`;

const SignInFormSlidingPanel = styled(SlidingPanel)`
  ${panelWithFooterStyles}
`;

const ForgotPasswordSlidingPanel = styled(SlidingPanel)``;

const EmailSentSlidingPanel = styled(SlidingPanel)``;

const PanelInnerWrapper = styled.div`
  padding: 0 var(--speedySignupPanelPadding, 52px);
  ${mobile`
    padding: 16px 24px;
  `}
`;

const SignInWrapper = styled.div`
  padding: 0;
  margin-bottom: 16px;
  ${mobile`
    padding: 16px;
  `}
`;

const CalloutMessage = styled(Text).attrs({
  forwardedAs: 'h3',
  size: 14,
  variant: 'modalCallout',
})`
  margin-top: 16px;
  margin-bottom: 2px;
  ${mobile`
    margin-top: 0;
  `}
`;

const Heading = styled(Text).attrs({
  forwardedAs: 'h2',
  variant: 'modalHeader',
})`
  margin-bottom: 12px;
`;

const Subheader = styled(Text).attrs({
  forwardedAs: 'h3',
  variant: 'modalSubheader',
})`
  margin-bottom: 24px;
`;

const Footer = styled.div`
  text-align: center;
  ${({ $spacedFooter, theme }) =>
    $spacedFooter
      ? css`
          display: flex;
          justify-content: space-between;
          align-items: center;
        `
      : css`
          button {
            margin-top: ${theme.sizes.sm}px;
          }
        `};
`;

const ControlButton = styled(Button).attrs({
  variant: 'unstyled',
})`
  border: none;
  width: fit-content;
  padding: 0;
  margin: 0;
`;

const ButtonLabelWrap = styled(Text)`
  display: flex;
  align-items: center;
`;

const SlidingPanelsControlButton = styled(ControlButton)`
  width: 100%;
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const BackToSignInControlButton = styled(SlidingPanelsControlButton)`
  margin-top: 16px;
`;

const TermsPrivacyWrapper = styled.div`
  flex 0 0 auto;
  position: sticky;
  bottom: 0;
  display: flex;
  justify-content: space-evenly;
  align-items: center;
  width: 100%;
  height: 40px;
  background: ${({ theme }) => theme.colors.backgroundLight};
  border-top: 2px solid ${({ theme }) => theme.colors.borderLight};
  color: ${({ theme }) => theme.colors.speedySignupTermsWrapper};
  text-align: center;
  &&& {
    max-height: 40px;
  }
`;

const TermsPrivacyLink = styled.div`
  padding: 12px;
`;

const Divider = styled.span`
  align-self: stretch;
  min-height: 100%;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

const StyledSlidingPanels = styled(SlidingPanels)`
  height: 100%;
  flex: 0 1 100%;
  overflow: auto;
`;

const fullHeight = {
  height: '100%',
};

const ProductOverlayMobileWrapper = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  margin-top: 32px;
  padding: 8px;
  display: flex;
`;

const ProductImageWrapper = styled.div`
  max-width: 25%;
  flex-shrink: 0;
  margin-right: 8px;
  border-radius: 2px;
`;

const ProductOverlayWrapper = styled.div`
  width: 100%;
`;

const ProductImage = styled(BaseProductImage)`
  height: 100%;

  picture {
    height: 100%;
  }

  img {
    min-height: 100%;
    object-fit: cover;
  }
`;

const SignInProductWrapper = styled.div`
  margin-top: 16px;
  margin-bottom: 16px;
  ${mobile`
    padding: 16px;
  `}
`;

const FormattedSpeedySignupForm = styled(SpeedySignupForm)`
  margin-top: 16px;
`;

const StyledOverflowIndicator = styled(OverflowIndicator)`
  flex: 0 1 auto;
  height: inherit;
  ${OverflowIndicator.Content} {
    display: flex !important;
    flex-flow: column nowrap;
    justify-content: space-between;
    align-items: stretch;
    height: auto;
    ${mobile`
      min-height: 100%;
    `}
    ${desktop`
      padding: var(--speedySignupPanelPadding) 0;
    `}
  }
`;

const GoBackToSignUpWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 var(--speedySignupPanelPadding, 52px);

  ${({ theme }) => theme.speedySignup.goBackToSignUpWrapperAdditionalStyle}
`;

const DynamicFormattedMessage = FormattedMessage;

const SignInFooter = ({ handleClick }) => (
  <Footer>
    <FormattedMessage
      id="site_login.not_a_member"
      defaultMessage="Not a Member?"
    />{' '}
    <BackToSignInControlButton
      data-autotag="SpeedySignupForm-Sign-Up-Toggle"
      onClick={() => handleClick(PanelStates.SIGN_UP)}
    >
      <FormattedMessage
        id="speedy_signup.sign_up_here"
        defaultMessage="Sign Up Here"
      />
    </BackToSignInControlButton>
  </Footer>
);

SignInFooter.propTypes = {
  handleClick: PropTypes.func.isRequired,
};

const TermsOfService = () => {
  return (
    <TermsPrivacyLink>
      <Link
        to="/terms"
        passHref
        prefetch={false}
        data-autotag="pdp_speedy_sign_up_terms_of_service"
        target="_blank"
        variant="linkSpeedyTermsAndPrivacy"
      >
        <FormattedMessage
          id="speedy_signup.terms_of_service"
          defaultMessage="Terms of Service"
        />
      </Link>
    </TermsPrivacyLink>
  );
};

const PrivacyPolicy = () => {
  return (
    <TermsPrivacyLink>
      <Link
        to="/privacy"
        passHref
        prefetch={false}
        data-autotag="pdp_speedy_sign_up_privacy_policy"
        target="_blank"
        variant="linkSpeedyTermsAndPrivacy"
      >
        <FormattedMessage
          id="speedy_signup.privacy_policy"
          defaultMessage="Privacy Policy"
        />
      </Link>
    </TermsPrivacyLink>
  );
};

const SpeedySignupPanels = (props) => {
  const { onSignupSuccess: onSignupSuccessFromProps } = props;
  const { addToCart } = useProductDetailCTAContext();
  const { isPlusSizeSelected, product } = useProductDetailContext();
  const [activeIndex, setActiveIndex] = useState(0);
  const panelRef = useRef(null);
  const { tld } = useDomain();
  const { isMobile } = useBreakpoint();
  const isUS = tld === '.com';
  const gender = useGuessLoggedOutCustomerGenderFromProduct();
  const [isWelcomeBackDisplayed, setIsWelcomeBackDisplayed] = useState(false);

  const defaultGetPromoCodeMessageId = (promoCode) => {
    return `site_pdp_callout.price_label_${promoCode}`;
  };

  const scrollToPanel = () => {
    scrollToRef(panelRef);
  };

  const setActivePanel = useCallback((index = PanelStates.SIGN_UP) => {
    setActiveIndex(index);
    scrollToPanel();
  }, []);

  const setEmailSent = useCallback(() => {
    setActivePanel(PanelStates.EMAIL_SENT);
  }, [setActivePanel]);

  const possiblyRedirect = useCallback(() => {
    if (speedySignupLoginRedirect.enabled) {
      const redirectUrl = speedySignupLoginRedirect.redirectUrls[gender];
      Router.push(redirectUrl);
    }
  }, [gender]);

  const onWelcomeBackUser = useCallback(() => {
    setIsWelcomeBackDisplayed(true);
  }, []);

  const onSignupSuccess = useCallback(() => {
    possiblyRedirect();
    onSignupSuccessFromProps();
  }, [onSignupSuccessFromProps, possiblyRedirect]);

  const onLoginSuccess = useCallback(() => {
    addToCart();
    // refresh the cart here --> something is wrong with the cart. Create potential followup ticket
    // invalidateCart();
    possiblyRedirect();
  }, [addToCart, possiblyRedirect]);

  const showProductOverlay = !speedySignupLayoutEnhanced && isMobile;
  const bannerImageBreakpoints = [{ minWidth: 0, pictureWidth: 400 }];

  // non-enhanced layout shows the overlay without image on mobile
  // see SpeedySignupModal for the enhanced layout and desktop layout
  const productOverlayMobileLayout = (
    <ProductOverlayMobileWrapper>
      <ProductImageWrapper>
        <ProductImage
          breakpoints={bannerImageBreakpoints}
          type={isPlusSizeSelected ? 'plus' : 'default'}
          disableHover
        />
      </ProductImageWrapper>
      <ProductOverlayWrapper>
        <ProductOverlay product={product} />
      </ProductOverlayWrapper>
    </ProductOverlayMobileWrapper>
  );

  const promoPricingConfig = config.get('public.brand.promoPricingUI');

  const offerLabelContainerName = getPromoPricingUIAssetContainerName(
    promoPricingConfig,
    false
  );
  const assetContainerOfferLabel = useAsset(offerLabelContainerName);
  const isPrioritizedEnabled =
    useHasFeature(featureNames.PRIORITIZED_PROMOS) &&
    promoPricingConfig?.showPromoPricingOffer;

  const promoPricingActiveAsset = getPromoPricingActiveAsset({
    isPrioritizedEnabled,
    assetContainerOfferLabel,
    product,
  });

  let htmlText = promoPricingActiveAsset?.options?.htmlText?.trim();
  htmlText = htmlText ? `${htmlText}: ` : null;

  const gridLabel = promoPricingActiveAsset?.options?.customVars?.gridLabel;

  const promoCode = product?.promos?.promoCode;
  const promoLabel = product?.promos?.promoLabel || '';
  const hasPromo = Boolean(promoCode) && typeof promoCode === 'string';

  const shouldShowOfferLabel =
    isPrioritizedEnabled && Boolean(htmlText || gridLabel);
  const offerLabel = `${htmlText}${gridLabel}`;

  const shouldShowPromoLabel =
    (!shouldShowOfferLabel && promoCode && promoLabel) ||
    (!shouldShowOfferLabel && hasPromo);

  return (
    <SlidingPanelsSection ref={panelRef} tabIndex={-1} {...props}>
      <StyledSlidingPanels
        activeIndex={activeIndex}
        panelStyle={fullHeight}
        sliderStyle={fullHeight}
        onPanelChanged={() => {
          // Put the keyboard focus in the right spot to continue tabbing after
          // the panel transition
          panelRef.current.focus();
        }}
      >
        <SignUpFormSlidingPanel>
          <StyledOverflowIndicator>
            <PanelInnerWrapper>
              {speedySignupLayoutEnhanced && (
                <CalloutMessage>
                  <FormattedMessage
                    id="speedy_signup.new_vip_member_exclusive"
                    defaultMessage="New VIP Member Exclusive"
                  />
                </CalloutMessage>
              )}
              <Heading>
                {shouldShowOfferLabel && (
                  <span
                    dangerouslySetInnerHTML={{ __html: `${offerLabel}:` }}
                  />
                )}
                {shouldShowPromoLabel && (
                  <DynamicFormattedMessage
                    id={defaultGetPromoCodeMessageId(promoCode)}
                    defaultMessage={promoLabel}
                  />
                )}
                {!shouldShowOfferLabel && !shouldShowPromoLabel && (
                  <FormattedMessage
                    id="speedy_signup.promo_message_heading"
                    defaultMessage="Get 40% off this style"
                  />
                )}
              </Heading>
              {!isWelcomeBackDisplayed && (
                <Subheader>
                  <FormattedMessage
                    id="speedy_signup.sign_in_or_sign_up"
                    defaultMessage="Sign in or sign up to unlock this deal!"
                  />
                </Subheader>
              )}
              {showProductOverlay && productOverlayMobileLayout}
              {isWelcomeBackDisplayed ? (
                <WelcomeBackWrapper />
              ) : (
                <>
                  <FormattedSpeedySignupForm
                    onWelcomeBackUser={onWelcomeBackUser}
                    onSignupSuccess={onSignupSuccess}
                  />
                  <Footer $spacedFooter={!speedySignupLayoutEnhanced}>
                    <Text variant="labelUnstyled">
                      <FormattedMessage
                        id="site_login.already_a_member"
                        defaultMessage="Already a member?"
                      />{' '}
                    </Text>
                    <ControlButton
                      data-autotag="SpeedySignupForm-Sign-In-Toggle"
                      onClick={() => setActivePanel(PanelStates.LOGIN)}
                    >
                      <Text variant="linkEmphasized">
                        <FormattedMessage
                          id="site_login.Sign_In"
                          defaultMessage="Sign In"
                        />
                        <FaChevronRight size={10} />
                      </Text>
                    </ControlButton>
                  </Footer>
                </>
              )}
            </PanelInnerWrapper>
          </StyledOverflowIndicator>
        </SignUpFormSlidingPanel>
        <SignInFormSlidingPanel>
          <StyledOverflowIndicator>
            <SignInWrapper>
              {showProductOverlay && (
                <SignInProductWrapper>
                  {productOverlayMobileLayout}
                </SignInProductWrapper>
              )}
              <LoginSlidingPanels
                onLoginSuccess={onLoginSuccess}
                hideGoBackCTA
                hideGetStartedCTA
                hideAutologinCTA
              />
              <GoBackToSignUpWrapper>
                <Text variant="labelUnstyled">
                  <FormattedMessage
                    id="site_login.not_a_member"
                    onSignupSuccess
                    defaultMessage="Not a Member?"
                  />{' '}
                </Text>
                <ControlButton
                  data-autotag="SpeedySignupForm-Sign-Up-Toggle"
                  onClick={() => setActivePanel(PanelStates.SIGN_UP)}
                >
                  <Text variant="linkEmphasized">
                    <FormattedMessage
                      id="speedy_signup.go_back_to_sign_up"
                      defaultMessage="Go Back to Sign Up"
                    />
                    <FaChevronLeft size={10} />
                  </Text>
                </ControlButton>
              </GoBackToSignUpWrapper>
            </SignInWrapper>
          </StyledOverflowIndicator>
        </SignInFormSlidingPanel>
        <ForgotPasswordSlidingPanel>
          <StyledOverflowIndicator>
            <PanelInnerWrapper>
              <Heading>
                <FormattedMessage
                  id="site_login.forgot_password1"
                  defaultMessage="Forgot your password?"
                />
              </Heading>
              <Subheader>
                <FormattedMessage
                  id="site_login.forgot_password2"
                  defaultMessage="Enter your email address below to reset your password."
                />
              </Subheader>
              {showProductOverlay && productOverlayMobileLayout}
              <ForgotPasswordForm onEmailSent={setEmailSent} />
              <BackToSignInControlButton
                data-autotag="back_to_signin"
                onClick={() => setActivePanel(PanelStates.LOGIN)}
              >
                <ButtonLabelWrap variant="linkEmphasized">
                  <FaChevronLeft size="14" />
                  <FormattedMessage
                    id="site_login.login_form_link"
                    defaultMessage="Back to Sign In"
                  />
                </ButtonLabelWrap>
              </BackToSignInControlButton>
            </PanelInnerWrapper>
          </StyledOverflowIndicator>
        </ForgotPasswordSlidingPanel>
        <EmailSentSlidingPanel>
          <StyledOverflowIndicator>
            <PanelInnerWrapper>
              <Heading>
                <FormattedMessage
                  id="site_login.email_sent"
                  defaultMessage="Email Sent!"
                />
              </Heading>
              <Subheader data-autotag="forgot_pw_success_msg">
                <FormattedMessage
                  id="site_login.check_your_password_sent1"
                  defaultMessage="Check your email"
                />{' '}
                <FormattedMessage
                  id="site_login.your_password_sent_2"
                  defaultMessage="and follow the instructions to reset your password."
                />
              </Subheader>
              <BackToSignInControlButton
                onClick={() => setActivePanel(PanelStates.LOGIN)}
              />
            </PanelInnerWrapper>
          </StyledOverflowIndicator>
        </EmailSentSlidingPanel>
      </StyledSlidingPanels>
      {isUS && (
        <TermsPrivacyWrapper>
          <TermsOfService />
          <Divider />
          <PrivacyPolicy />
        </TermsPrivacyWrapper>
      )}
    </SlidingPanelsSection>
  );
};

SpeedySignupPanels.propTypes = {
  onSignupSuccess: PropTypes.func,
};

export default SpeedySignupPanels;
